import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { fmtBytes, fmtRelative } from "@/lib/format";
import { Users, FolderKanban, ListChecks, FileImage, ArrowUpRight, CheckCircle2, Share2 } from "lucide-react";

export default function Dashboard() {
  const { activeWorkspaceId, activeWorkspace } = useAuth();
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [linkStats, setLinkStats] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/dashboard`).then((r) => setData(r.data));
    api.get(`/workspaces/${activeWorkspaceId}/projects`).then((r) => setRecent((r.data || []).slice(0, 5)));
    api.get(`/workspaces/${activeWorkspaceId}/link-analytics`).then((r) => setLinkStats(r.data)).catch(() => {});
  }, [activeWorkspaceId]);

  if (!activeWorkspaceId) {
    return (
      <div className="p-10">
        <EmptyState
          title="Create your first workspace"
          description="You haven't joined or created a workspace yet."
          action={<Button onClick={() => nav("/onboarding")} data-testid="dash-create-ws-btn">Create workspace</Button>}
          testid="dash-empty-ws"
        />
      </div>
    );
  }

  const s = data?.stats || {};
  const seatPct = s.seat_limit ? Math.min(100, (s.seats_used / s.seat_limit) * 100) : 0;
  const storagePct = s.storage_limit_gb ? Math.min(100, (s.storage_used_gb / s.storage_limit_gb) * 100) : 0;

  return (
    <>
      <PageHeader
        eyebrow={`Workspace · ${activeWorkspace?.currency || "INR"}`}
        title={activeWorkspace?.name || "Dashboard"}
        description={`Plan: ${data?.plan?.name || "Free"} — usage at a glance.`}
        testid="dashboard-header"
        actions={
          <>
            <Button variant="outline" onClick={() => nav("/app/clients")} className="rounded-none" data-testid="dash-new-client-btn">New client</Button>
            <Button onClick={() => nav("/app/projects")} className="rounded-none" data-testid="dash-new-project-btn">New project</Button>
          </>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 md:py-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-12 gap-3">
        <StatCard label="Clients" value={s.clients ?? 0} icon={Users} testid="stat-clients" />
        <StatCard label="Projects" value={s.projects ?? 0} icon={FolderKanban} testid="stat-projects" />
        <StatCard label="Review items" value={s.review_items ?? 0} icon={FileImage} testid="stat-items" />
        <StatCard label="Open tasks" value={s.open_tasks ?? 0} icon={ListChecks} testid="stat-tasks" />
        <StatCard label="Pending approvals" value={s.pending_approvals ?? 0} icon={CheckCircle2} testid="stat-approvals" highlight />

        <QuotaCard
          label="Seats"
          used={s.seats_used ?? 0}
          limit={s.seat_limit ?? 0}
          pct={seatPct}
          format={(v) => v}
          testid="quota-seats"
        />
        <QuotaCard
          label="Storage"
          used={s.storage_used_gb ?? 0}
          limit={s.storage_limit_gb ?? 0}
          pct={storagePct}
          format={(v) => `${v.toFixed(2)} GB`}
          testid="quota-storage"
        />
      </div>

      <div className="px-4 sm:px-6 md:px-10 pb-12 grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg" style={{ fontFamily: "var(--font-heading)" }}>Recent projects</h3>
            <Link to="/app/projects" className="text-xs label-eyebrow hover:text-primary" data-testid="dash-see-all-projects">All projects →</Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">No projects yet. Create one to get started.</div>
          ) : (
            <ul className="divide-y divide-border">
              {recent.map((p) => (
                <li key={p.id} className="py-3 flex items-center justify-between" data-testid={`recent-project-${p.id}`}>
                  <div className="min-w-0">
                    <Link to={`/app/projects/${p.id}`} className="font-medium hover:text-primary truncate block">{p.name}</Link>
                    <div className="text-xs text-muted-foreground">{p.client?.name} · {p.review_item_count} items · created {fmtRelative(p.created_at)}</div>
                  </div>
                  <Link to={`/app/projects/${p.id}`}><ArrowUpRight className="w-4 h-4 text-muted-foreground hover:text-primary" /></Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-3">
          {linkStats && (
            <div className="border border-border p-6" data-testid="dash-link-analytics">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow flex items-center gap-2"><Share2 className="w-3.5 h-3.5" /> Share-link analytics</div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }} data-testid="link-stat-total">{linkStats.total_links}</div>
                  <div className="label-eyebrow text-[9px] mt-1">Links</div>
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }} data-testid="link-stat-opens">{linkStats.total_opens}</div>
                  <div className="label-eyebrow text-[9px] mt-1">Opens</div>
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }} data-testid="link-stat-rate">{linkStats.open_rate_pct}%</div>
                  <div className="label-eyebrow text-[9px] mt-1">Open rate</div>
                </div>
              </div>
            </div>
          )}
          <div className="border border-border p-6 bg-foreground text-background">
            <div className="label-eyebrow opacity-70 mb-3">Quick start</div>
            <h3 className="font-bold text-xl mb-3" style={{ fontFamily: "var(--font-heading)" }}>Ship your first review.</h3>
            <ol className="text-sm space-y-2 opacity-90 mb-4">
              <li>1. Create a client</li>
              <li>2. Add a project</li>
              <li>3. Upload an image or PDF</li>
              <li>4. Share the secure review link</li>
              <li>5. Collect approval</li>
            </ol>
            <Button onClick={() => nav("/app/clients")} className="rounded-none bg-primary text-primary-foreground w-full" data-testid="dash-quickstart-btn">Start now</Button>
          </div>
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, icon: Icon, highlight, testid }) {
  return (
    <div className={`lg:col-span-2 border border-border p-5 ${highlight ? "bg-foreground text-background" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-eyebrow" style={{ color: highlight ? "rgba(255,255,255,0.7)" : undefined }}>{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-60" />}
      </div>
      <div className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function QuotaCard({ label, used, limit, pct, format, testid }) {
  return (
    <div className="lg:col-span-6 border border-border p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-eyebrow">{label}</span>
        <span className="text-xs text-muted-foreground font-mono">{format(used)} / {limit === 0 ? "Custom" : format(limit)}</span>
      </div>
      <Progress value={pct} className="h-1.5" />
    </div>
  );
}
